/**
 * VP Tailwind Theme - Main JavaScript
 * Vascular Pump - Premium Automotive Products
 * 
 * Theme.js - Core functionality for the Vascular Pump Shopify Theme
 */

(function() {
  'use strict';

  // ================================
  // THEME CONFIGURATION
  // ================================
  const Theme = {
    config: {
      animationDuration: 300,
      scrollOffset: 100,
      breakpoints: {
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        '2xl': 1536
      }
    },

    // ================================
    // INITIALIZATION
    // ================================
    init: function() {
      this.initMobileMenu();
      this.initSmoothScroll();
      this.initScrollAnimations();
      this.initCartDrawer();
      this.initProductForms();
      this.initQuantitySelectors();
      this.initAccordions();
      this.initTabs();
      this.initImageZoom();
      this.initLazyLoad();
      this.initStickyHeader();
      this.initAnnouncementBar();
    },

    // ================================
    // MOBILE MENU
    // ================================
    initMobileMenu: function() {
      const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
      const mobileMenu = document.querySelector('[data-mobile-menu]');
      const closeBtn = document.querySelector('[data-mobile-menu-close]');
      
      if (!menuToggle || !mobileMenu) return;

      const openMenu = () => {
        mobileMenu.classList.remove('hidden');
        mobileMenu.classList.add('flex');
        document.body.style.overflow = 'hidden';
        menuToggle.setAttribute('aria-expanded', 'true');
      };

      const closeMenu = () => {
        mobileMenu.classList.add('hidden');
        mobileMenu.classList.remove('flex');
        document.body.style.overflow = '';
        menuToggle.setAttribute('aria-expanded', 'false');
      };

      menuToggle.addEventListener('click', openMenu);
      
      if (closeBtn) {
        closeBtn.addEventListener('click', closeMenu);
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
          closeMenu();
        }
      });

      // Close on backdrop click
      mobileMenu.addEventListener('click', (e) => {
        if (e.target === mobileMenu) {
          closeMenu();
        }
      });
    },

    // ================================
    // SMOOTH SCROLL
    // ================================
    initSmoothScroll: function() {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', (e) => {
          const targetId = anchor.getAttribute('href');
          if (targetId === '#') return;
          
          const target = document.querySelector(targetId);
          if (target) {
            e.preventDefault();
            const headerHeight = document.querySelector('[data-header]')?.offsetHeight || 0;
            const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        });
      });
    },

    // ================================
    // SCROLL ANIMATIONS
    // ================================
    initScrollAnimations: function() {
      const animatedElements = document.querySelectorAll('[data-animate]');
      
      if (!animatedElements.length) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const animation = entry.target.dataset.animate || 'fade-in';
            entry.target.classList.add(`animate-${animation}`);
            entry.target.classList.remove('opacity-0');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animatedElements.forEach(el => {
        el.classList.add('opacity-0');
        observer.observe(el);
      });
    },

    // ================================
    // CART DRAWER
    // ================================
    initCartDrawer: function() {
      const cartDrawer = document.querySelector('[data-cart-drawer]');
      const cartToggle = document.querySelectorAll('[data-cart-toggle]');
      const cartClose = document.querySelector('[data-cart-close]');
      const cartOverlay = document.querySelector('[data-cart-overlay]');
      
      if (!cartDrawer) return;

      const openCart = () => {
        cartDrawer.classList.remove('translate-x-full');
        cartOverlay?.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
      };

      const closeCart = () => {
        cartDrawer.classList.add('translate-x-full');
        cartOverlay?.classList.add('hidden');
        document.body.style.overflow = '';
      };

      cartToggle.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          openCart();
        });
      });

      cartClose?.addEventListener('click', closeCart);
      cartOverlay?.addEventListener('click', closeCart);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !cartDrawer.classList.contains('translate-x-full')) {
          closeCart();
        }
      });

      // Custom event to open cart
      document.addEventListener('cart:open', openCart);
      document.addEventListener('cart:close', closeCart);
    },

    // ================================
    // PRODUCT FORMS
    // ================================
    initProductForms: function() {
      const productForms = document.querySelectorAll('[data-product-form]');
      
      productForms.forEach(form => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          
          const submitBtn = form.querySelector('[type="submit"]');
          const originalText = submitBtn.textContent;
          
          // Show loading state
          submitBtn.disabled = true;
          submitBtn.textContent = 'Adding...';
          
          try {
            const formData = new FormData(form);
            
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              body: formData
            });
            
            if (!response.ok) throw new Error('Failed to add to cart');
            
            const item = await response.json();
            
            // Update cart count and drawer contents
            this.updateCartCount();
            await this.updateCartDrawer();
            
            // Open cart drawer
            document.dispatchEvent(new CustomEvent('cart:open'));
            
            // Success feedback
            submitBtn.textContent = 'Added!';
            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
            }, 2000);
            
          } catch (error) {
            submitBtn.textContent = 'Error';
            setTimeout(() => {
              submitBtn.textContent = originalText;
              submitBtn.disabled = false;
            }, 2000);
          }
        });
      });
    },

    // ================================
    // UPDATE CART COUNT
    // ================================
    updateCartCount: async function() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const cartCounts = document.querySelectorAll('[data-cart-count]');
        cartCounts.forEach(el => {
          el.textContent = cart.item_count;
          el.classList.toggle('hidden', cart.item_count === 0);
        });
      } catch (error) {
        // Silently handle cart count update errors
      }
    },

    // ================================
    // UPDATE CART DRAWER CONTENTS
    // ================================
    updateCartDrawer: async function() {
      try {
        const response = await fetch('/?section_id=cart-drawer-content');
        const html = await response.text();
        
        // Parse the HTML and extract cart contents
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContents = doc.querySelector('[data-cart-contents]');
        const newFooter = doc.querySelector('[data-cart-footer]');
        
        // Update cart contents
        const cartContents = document.querySelector('[data-cart-contents]');
        if (cartContents && newContents) {
          cartContents.innerHTML = newContents.innerHTML;
        }
        
        // Update cart footer (subtotal and checkout)
        const cartFooter = document.querySelector('[data-cart-footer]');
        if (newFooter) {
          if (cartFooter) {
            cartFooter.innerHTML = newFooter.innerHTML;
          } else {
            // Footer doesn't exist, append it
            const cartPanel = document.querySelector('[data-cart-panel]');
            if (cartPanel) {
              const footerDiv = document.createElement('div');
              footerDiv.setAttribute('data-cart-footer', '');
              footerDiv.innerHTML = newFooter.innerHTML;
              cartPanel.appendChild(footerDiv);
            }
          }
        } else if (cartFooter) {
          // No footer in response (cart is empty), remove existing footer
          cartFooter.remove();
        }
      } catch (error) {
        // Fallback: reload the page if section fetch fails
        console.error('Failed to update cart drawer:', error);
      }
    },

    // ================================
    // QUANTITY SELECTORS
    // ================================
    initQuantitySelectors: function() {
      document.querySelectorAll('[data-quantity-selector]').forEach(selector => {
        const input = selector.querySelector('input[type="number"]');
        const decreaseBtn = selector.querySelector('[data-decrease]');
        const increaseBtn = selector.querySelector('[data-increase]');
        
        if (!input) return;

        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 999;

        const updateValue = (newValue) => {
          const value = Math.max(min, Math.min(max, newValue));
          input.value = value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        };

        decreaseBtn?.addEventListener('click', () => {
          updateValue(parseInt(input.value) - 1);
        });

        increaseBtn?.addEventListener('click', () => {
          updateValue(parseInt(input.value) + 1);
        });

        input.addEventListener('change', () => {
          updateValue(parseInt(input.value) || min);
        });
      });
    },

    // ================================
    // ACCORDIONS
    // ================================
    initAccordions: function() {
      document.querySelectorAll('[data-accordion]').forEach(accordion => {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        const content = accordion.querySelector('[data-accordion-content]');
        const icon = accordion.querySelector('[data-accordion-icon]');
        
        if (!trigger || !content) return;

        trigger.addEventListener('click', () => {
          const isOpen = content.classList.contains('hidden');
          
          if (isOpen) {
            content.classList.remove('hidden');
            icon?.classList.add('rotate-180');
            trigger.setAttribute('aria-expanded', 'true');
          } else {
            content.classList.add('hidden');
            icon?.classList.remove('rotate-180');
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
      });
    },

    // ================================
    // TABS
    // ================================
    initTabs: function() {
      document.querySelectorAll('[data-tabs]').forEach(tabContainer => {
        const tabs = tabContainer.querySelectorAll('[data-tab]');
        const panels = tabContainer.querySelectorAll('[data-tab-panel]');
        
        tabs.forEach(tab => {
          tab.addEventListener('click', () => {
            const targetId = tab.dataset.tab;
            
            // Update tabs
            tabs.forEach(t => {
              t.classList.remove('border-primary', 'text-white');
              t.classList.add('border-transparent', 'text-gray-500');
              t.setAttribute('aria-selected', 'false');
            });
            
            tab.classList.add('border-primary', 'text-white');
            tab.classList.remove('border-transparent', 'text-gray-500');
            tab.setAttribute('aria-selected', 'true');
            
            // Update panels
            panels.forEach(panel => {
              panel.classList.toggle('hidden', panel.dataset.tabPanel !== targetId);
            });
          });
        });
      });
    },

    // ================================
    // IMAGE ZOOM
    // ================================
    initImageZoom: function() {
      document.querySelectorAll('[data-image-zoom]').forEach(container => {
        const image = container.querySelector('img');
        if (!image) return;

        container.addEventListener('mousemove', (e) => {
          const rect = container.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          
          image.style.transformOrigin = `${x}% ${y}%`;
          image.style.transform = 'scale(1.5)';
        });

        container.addEventListener('mouseleave', () => {
          image.style.transform = 'scale(1)';
        });
      });
    },

    // ================================
    // LAZY LOAD
    // ================================
    initLazyLoad: function() {
      if ('loading' in HTMLImageElement.prototype) {
        // Native lazy loading supported
        document.querySelectorAll('img[loading="lazy"]').forEach(img => {
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
        });
      } else {
        // Fallback to Intersection Observer
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
              }
              imageObserver.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      }
    },

    // ================================
    // STICKY HEADER
    // ================================
    initStickyHeader: function() {
      const header = document.querySelector('[data-header]');
      if (!header) return;

      let lastScroll = 0;
      const headerHeight = header.offsetHeight;

      window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;
        
        if (currentScroll <= 0) {
          header.classList.remove('-translate-y-full');
          return;
        }

        if (currentScroll > lastScroll && currentScroll > headerHeight) {
          // Scrolling down
          header.classList.add('-translate-y-full');
        } else {
          // Scrolling up
          header.classList.remove('-translate-y-full');
        }

        lastScroll = currentScroll;
      }, { passive: true });
    },

    // ================================
    // ANNOUNCEMENT BAR
    // ================================
    initAnnouncementBar: function() {
      const dismissBtn = document.querySelector('[data-announcement-dismiss]');
      const announcementBar = document.querySelector('[data-announcement-bar]');
      
      if (!dismissBtn || !announcementBar) return;

      // Check if already dismissed in this session
      if (sessionStorage.getItem('announcement-dismissed')) {
        announcementBar.classList.add('hidden');
        return;
      }

      dismissBtn.addEventListener('click', () => {
        announcementBar.classList.add('hidden');
        sessionStorage.setItem('announcement-dismissed', 'true');
      });
    },

    // ================================
    // UTILITY: DEBOUNCE
    // ================================
    debounce: function(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // ================================
    // UTILITY: THROTTLE
    // ================================
    throttle: function(func, limit) {
      let inThrottle;
      return function(...args) {
        if (!inThrottle) {
          func.apply(this, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },

    // ================================
    // UTILITY: FORMAT MONEY
    // ================================
    formatMoney: function(cents, format) {
      const formatString = format || '${{amount}}';
      
      const value = (cents / 100).toFixed(2);
      const [dollars, decimalCents] = value.split('.');
      
      return formatString
        .replace('{{amount}}', value)
        .replace('{{amount_no_decimals}}', dollars)
        .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
    },

    // ================================
    // UTILITY: FETCH SECTION
    // ================================
    fetchSection: async function(sectionId, url) {
      try {
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${sectionId}`);
        const text = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        return doc.querySelector(`#shopify-section-${sectionId}`);
      } catch (error) {
        return null;
      }
    }
  };

  // ================================
  // INITIALIZE ON DOM READY
  // ================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Theme.init());
  } else {
    Theme.init();
  }

  // Expose Theme globally for external access
  window.VPTheme = Theme;

})();

// ================================
// VP HEADER COMPONENT
// ================================
/**
 * VP Header Web Component
 * Handles header scroll effects and mobile menu toggle
 * - Sticky header with backdrop blur on scroll
 * - Mobile menu open/close functionality
 * - Smooth transitions and animations
 */
if (!customElements.get('vp-header-component')) {
  class VpHeader extends HTMLElement {
    constructor() {
      super();
      this.headerNav = this.querySelector('[data-header-nav]');
      this.mobileMenuBtn = this.querySelector('[data-mobile-menu-toggle]');
      this.mobileMenu = this.querySelector('[data-mobile-menu]');
      this.menuIcon = this.querySelector('.menu-icon');
      this.closeIcon = this.querySelector('.close-icon');
      this.isMenuOpen = false;
      
      this.init();
    }

    init() {
      // Check if this is homepage
      const isHomepage = this.headerNav && this.headerNav.dataset.isHomepage === 'true';
      
      // Only add scroll listener if header nav exists
      if (!this.headerNav) return;
      
      // Header scroll effect - different behavior for homepage vs other pages
      // Use different thresholds: homepage uses 50px, product pages use 20px
      const scrollThreshold = isHomepage ? 50 : 20;
      
      const handleScroll = () => {
        if (window.scrollY > scrollThreshold) {
          // Scrolled state - same for both
          this.headerNav.classList.add('bg-[#050505]/95', 'backdrop-blur-md', 'border-white/10', 'py-4');
          if (isHomepage) {
            // Homepage: remove transparent background and border
            this.headerNav.classList.remove('bg-transparent', 'border-transparent', 'py-6');
          } else {
            // Other pages: remove solid background and border (but keep sticky/top-0)
            this.headerNav.classList.remove('bg-[#050505]', 'border-white/5', 'py-6');
          }
        } else {
          // Top state - different for homepage vs other pages
          this.headerNav.classList.remove('bg-[#050505]/95', 'backdrop-blur-md', 'border-white/10', 'py-4');
          if (isHomepage) {
            // Homepage: restore transparent background and border
            this.headerNav.classList.add('bg-transparent', 'border-transparent', 'py-6');
          } else {
            // Other pages: restore solid background and border
            this.headerNav.classList.add('bg-[#050505]', 'border-b', 'border-white/5', 'py-6');
          }
        }
      };
      
      window.addEventListener('scroll', handleScroll);
      
      // Call once on load to set initial state
      handleScroll();

      // Mobile menu toggle button
      if (this.mobileMenuBtn) {
        this.mobileMenuBtn.addEventListener('click', () => this.toggleMenu());
      }

      // Close menu when clicking outside
      document.addEventListener('click', (e) => {
        if (this.isMenuOpen && !this.contains(e.target)) {
          this.closeMenu();
        }
      });

      // Close menu on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isMenuOpen) {
          this.closeMenu();
        }
      });
    }

    toggleMenu() {
      if (this.isMenuOpen) {
        this.closeMenu();
      } else {
        this.openMenu();
      }
    }

    openMenu() {
      this.isMenuOpen = true;
      
      // Animate menu icon to close icon
      this.menuIcon.style.transition = 'transform 0.3s ease, opacity 0.2s ease';
      this.closeIcon.style.transition = 'transform 0.3s ease, opacity 0.2s ease';
      
      this.menuIcon.style.transform = 'rotate(90deg)';
      this.menuIcon.style.opacity = '0';
      
      setTimeout(() => {
        this.menuIcon.classList.add('hidden');
        this.closeIcon.classList.remove('hidden');
        this.closeIcon.style.transform = 'rotate(-90deg)';
        this.closeIcon.style.opacity = '0';
        
        // Trigger reflow for animation
        this.closeIcon.offsetHeight;
        
        this.closeIcon.style.transform = 'rotate(0deg)';
        this.closeIcon.style.opacity = '1';
      }, 150);
      
      // Animate menu open
      this.mobileMenu.style.display = 'flex';
      
      // Trigger reflow for animation
      this.mobileMenu.offsetHeight;
      
      this.mobileMenu.classList.remove('opacity-0', 'invisible', 'max-h-0');
      this.mobileMenu.classList.add('opacity-100', 'visible', 'max-h-screen');
      
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    }

    closeMenu() {
      this.isMenuOpen = false;
      
      // Animate close icon to menu icon
      this.closeIcon.style.transform = 'rotate(90deg)';
      this.closeIcon.style.opacity = '0';
      
      setTimeout(() => {
        this.closeIcon.classList.add('hidden');
        this.menuIcon.classList.remove('hidden');
        this.menuIcon.style.transform = 'rotate(-90deg)';
        this.menuIcon.style.opacity = '0';
        
        // Trigger reflow for animation
        this.menuIcon.offsetHeight;
        
        this.menuIcon.style.transform = 'rotate(0deg)';
        this.menuIcon.style.opacity = '1';
      }, 150);
      
      // Animate menu close
      this.mobileMenu.classList.remove('opacity-100', 'visible', 'max-h-screen');
      this.mobileMenu.classList.add('opacity-0', 'invisible', 'max-h-0');
      
      // Re-enable body scroll
      document.body.style.overflow = '';
    }
  }

  customElements.define('vp-header-component', VpHeader);
}

// ================================
// MOBILE DROPDOWN MENUS
// ================================
/**
 * Mobile Navigation Dropdown Handler
 * Handles nested dropdown menus in mobile navigation
 */
document.addEventListener('DOMContentLoaded', function() {
  const mobileDropdownToggles = document.querySelectorAll('[data-mobile-dropdown-toggle]');
  
  mobileDropdownToggles.forEach(toggle => {
    toggle.addEventListener('click', function(e) {
      e.preventDefault();
      
      const dropdown = this.closest('[data-mobile-dropdown]');
      const content = dropdown.querySelector('[data-mobile-dropdown-content]');
      const arrow = this.querySelector('[data-dropdown-arrow]');
      
      if (!content) return;
      
      const isOpen = !content.classList.contains('hidden');
      
      if (isOpen) {
        // Close dropdown
        content.classList.add('hidden');
        content.classList.remove('flex');
        if (arrow) arrow.classList.remove('rotate-180');
      } else {
        // Open dropdown
        content.classList.remove('hidden');
        content.classList.add('flex');
        if (arrow) arrow.classList.add('rotate-180');
      }
    });
  });
});

// ================================
// MAIN CART - FAST QUANTITY UPDATER
// ================================
/**
 * Cart Quantity Update Function (Optimized)
 * Updates cart item quantity with instant UI feedback
 * @param {number} line - Line item index (1-based)
 * @param {number} quantity - New quantity (0 to remove item)
 */
(function() {
  let updateTimeout = null;
  let pendingUpdates = {};
  
  window.updateQuantity = function(line, quantity) {
    // Store pending update
    pendingUpdates[line] = quantity;
    
    // Get the cart item element
    const cartForm = document.getElementById('cart-form');
    const lineItem = cartForm?.querySelector(`[data-line-index="${line}"]`)?.closest('.flex.gap-6');
    
    // Instant UI feedback
    if (lineItem) {
      // Show loading state
      lineItem.style.opacity = '0.5';
      lineItem.style.pointerEvents = 'none';
      
      // Update quantity display immediately
      const qtyInput = lineItem.querySelector('input[type="number"]');
      if (qtyInput && quantity > 0) {
        qtyInput.value = quantity;
      }
      
      // If removing (quantity = 0), fade out immediately
      if (quantity === 0) {
        lineItem.style.transition = 'opacity 0.2s, transform 0.2s';
        lineItem.style.opacity = '0';
        lineItem.style.transform = 'translateX(-20px)';
      }
    }
    
    // Debounce the actual API call
    clearTimeout(updateTimeout);
    updateTimeout = setTimeout(async () => {
      try {
        // Process all pending updates
        const updates = { ...pendingUpdates };
        pendingUpdates = {};
        
        // Make API call for each update
        for (const [updateLine, updateQty] of Object.entries(updates)) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: parseInt(updateLine), quantity: updateQty })
          });
        }
        
        // Fetch updated cart section instead of full page reload
        const response = await fetch(`${window.location.pathname}?section_id=main-cart`);
        const html = await response.text();
        
        // Parse and update cart content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector('.main-cart-section');
        const currentContent = document.querySelector('.main-cart-section');
        
        if (newContent && currentContent) {
          currentContent.innerHTML = newContent.innerHTML;
        } else {
          // Fallback to page reload if section not found
          window.location.reload();
        }
        
        // Update cart count in header
        if (window.VPTheme) {
          window.VPTheme.updateCartCount();
        }
      } catch (error) {
        console.error('Cart update failed:', error);
        window.location.reload();
      }
    }, 300); // 300ms debounce
  };
})();

// ================================
// CART DRAWER - FAST QUANTITY UPDATER
// ================================
/**
 * Cart Drawer Quantity Update Function (Optimized)
 * Updates cart with instant UI feedback
 * @param {number} line - Line item index (1-based)
 * @param {number} quantity - New quantity (0 to remove item)
 */
(function() {
  let drawerUpdateTimeout = null;
  let drawerPendingUpdates = {};
  
  window.updateCartDrawerQuantity = function(line, quantity) {
    // Store pending update
    drawerPendingUpdates[line] = quantity;
    
    // Get the cart item element
    const cartItem = document.querySelector(`[data-cart-item][data-line="${line}"]`);
    
    // Instant UI feedback
    if (cartItem) {
      // Show loading state
      cartItem.style.opacity = '0.5';
      cartItem.style.pointerEvents = 'none';
      
      // Update quantity display immediately
      const qtyDisplay = cartItem.querySelector('.text-center');
      if (qtyDisplay && quantity > 0) {
        qtyDisplay.textContent = quantity;
      }
      
      // If removing, fade out immediately
      if (quantity === 0) {
        cartItem.style.transition = 'opacity 0.2s, transform 0.2s, height 0.3s';
        cartItem.style.opacity = '0';
        cartItem.style.transform = 'translateX(-20px)';
        setTimeout(() => {
          cartItem.style.height = '0';
          cartItem.style.overflow = 'hidden';
          cartItem.style.marginBottom = '0';
          cartItem.style.paddingTop = '0';
          cartItem.style.paddingBottom = '0';
        }, 200);
      }
    }
    
    // Debounce the actual API call
    clearTimeout(drawerUpdateTimeout);
    drawerUpdateTimeout = setTimeout(async () => {
      try {
        // Process all pending updates
        const updates = { ...drawerPendingUpdates };
        drawerPendingUpdates = {};
        
        // Make API call for each update
        for (const [updateLine, updateQty] of Object.entries(updates)) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: parseInt(updateLine), quantity: updateQty })
          });
        }
        
        // Update cart count immediately
        if (window.VPTheme) {
          window.VPTheme.updateCartCount();
        }
        
        // Fetch updated drawer content
        const response = await fetch('/?section_id=cart-drawer-content');
        const html = await response.text();
        
        // Parse and update
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newContents = doc.querySelector('[data-cart-contents]');
        const newFooter = doc.querySelector('[data-cart-footer]');
        const cartContents = document.querySelector('[data-cart-contents]');
        const cartFooter = document.querySelector('[data-cart-footer]');
        
        if (cartContents && newContents) {
          cartContents.innerHTML = newContents.innerHTML;
        }
        
        if (newFooter && cartFooter) {
          cartFooter.innerHTML = newFooter.innerHTML;
        } else if (!newFooter && cartFooter) {
          cartFooter.remove();
        } else if (newFooter && !cartFooter) {
          const cartPanel = document.querySelector('[data-cart-panel]');
          if (cartPanel) {
            cartPanel.insertAdjacentHTML('beforeend', newFooter.outerHTML);
          }
        }
      } catch (error) {
        console.error('Cart drawer update failed:', error);
      }
    }, 300); // 300ms debounce
  };
})();

// ================================
// VP RELATED PRODUCTS - QUICK ADD & SLIDER
// ================================
/**
 * Related Products Section Functionality
 * - Quick add to cart with AJAX
 * - Mobile scroll indicators
 */
document.addEventListener('DOMContentLoaded', function() {
  // Quick Add Forms
  const quickAddForms = document.querySelectorAll('.vp-quick-add-form');
  
  quickAddForms.forEach(form => {
    form.addEventListener('submit', async function(e) {
      e.preventDefault();
      
      const button = form.querySelector('[data-quick-add-btn]');
      const originalText = button.innerHTML;
      
      // Show loading state
      button.innerHTML = `
        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Adding...
      `;
      button.disabled = true;
      
      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) throw new Error('Failed to add to cart');
        
        // Success state
        button.innerHTML = `
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Added!
        `;
        button.classList.remove('bg-red-600', 'hover:bg-red-700');
        button.classList.add('bg-green-600');
        
        // Update cart count if VPTheme is available
        if (window.VPTheme && window.VPTheme.updateCartCount) {
          window.VPTheme.updateCartCount();
        }
        
        // Open cart drawer if available
        const cartDrawer = document.querySelector('[data-cart-drawer]');
        if (cartDrawer && window.VPTheme && window.VPTheme.openCartDrawer) {
          window.VPTheme.openCartDrawer();
        }
        
        // Reset button after delay
        setTimeout(() => {
          button.innerHTML = originalText;
          button.disabled = false;
          button.classList.remove('bg-green-600');
          button.classList.add('bg-red-600', 'hover:bg-red-700');
        }, 2000);
        
      } catch (error) {
        // Error state
        button.innerHTML = `
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          Error
        `;
        button.classList.remove('bg-red-600', 'hover:bg-red-700');
        button.classList.add('bg-gray-600');
        
        setTimeout(() => {
          button.innerHTML = originalText;
          button.disabled = false;
          button.classList.remove('bg-gray-600');
          button.classList.add('bg-red-600', 'hover:bg-red-700');
        }, 2000);
      }
    });
  });
  
  // Mobile scroll indicators
  const relatedSliders = document.querySelectorAll('[data-related-slider]');
  
  relatedSliders.forEach(slider => {
    const section = slider.closest('[data-related-products]');
    if (!section) return;
    
    const indicators = section.querySelectorAll('.scroll-indicator');
    if (indicators.length === 0) return;
    
    // Update indicators on scroll
    slider.addEventListener('scroll', function() {
      const scrollLeft = slider.scrollLeft;
      const itemWidth = 280 + 24; // width + gap
      const activeIndex = Math.round(scrollLeft / itemWidth);
      
      indicators.forEach((indicator, index) => {
        if (index === activeIndex) {
          indicator.classList.remove('bg-white/20');
          indicator.classList.add('bg-white');
        } else {
          indicator.classList.remove('bg-white');
          indicator.classList.add('bg-white/20');
        }
      });
    });
    
    // Set first indicator as active initially
    if (indicators[0]) {
      indicators[0].classList.remove('bg-white/20');
      indicators[0].classList.add('bg-white');
    }
  });
});

// ================================
// MAIN PRODUCT - GALLERY & CONTROLS
// ================================
/**
 * Product Page Functionality
 * - Thumbnail gallery image switching
 * - Quantity increment/decrement controls
 */
document.addEventListener('DOMContentLoaded', function() {
  // ================================
  // Thumbnail Gallery
  // ================================
  const thumbnails = document.querySelectorAll('[data-thumbnail]');
  const mainImage = document.getElementById('product-main-image');
  
  if (thumbnails.length && mainImage) {
    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', function() {
        // Remove active state from all thumbnails
        thumbnails.forEach(t => {
          t.classList.remove('border-red-500');
          t.classList.add('border-transparent');
        });
        
        // Add active state to clicked thumbnail
        this.classList.remove('border-transparent');
        this.classList.add('border-red-500');
        
        // Update main image
        mainImage.src = this.dataset.imageUrl;
      });
    });
  }

  // ================================
  // Quantity Controls
  // ================================
  const quantityInput = document.getElementById('product-quantity');
  const minusBtn = document.querySelector('[data-quantity-minus]');
  const plusBtn = document.querySelector('[data-quantity-plus]');

  if (minusBtn && plusBtn && quantityInput) {
    // Decrease quantity
    minusBtn.addEventListener('click', () => {
      const current = parseInt(quantityInput.value);
      if (current > 1) quantityInput.value = current - 1;
    });

    // Increase quantity
    plusBtn.addEventListener('click', () => {
      const current = parseInt(quantityInput.value);
      quantityInput.value = current + 1;
    });
  }
});

// ================================
// VP PRODUCT SECTION - FLAVOR SWITCHER
// ================================
/**
 * VP Product Section - Dual Product Display with Flavor Picker
 * Features:
 * - Switch between multiple products
 * - Select different flavors/variants per product
 * - Dynamic color theme changes based on flavor selection
 * - Animated glow effects and color transitions
 * - Updates product images, prices, and descriptions
 */
function initVPProductSection() {
  const section = document.querySelector('[data-product-section]');
  if (!section) {
    return;
  }

  // ================================
  // Color Configuration
  // ================================
  // Define color schemes for different flavor types
  const colorConfig = {
    red: { rgb: '220, 38, 38', hex: '#dc2626' },      // Watermelon, Cherry, Strawberry
    orange: { rgb: '249, 115, 22', hex: '#f97316' },   // Mango, Orange, Citrus
    blue: { rgb: '59, 130, 246', hex: '#3b82f6' },     // Blue Raspberry, Berry
    purple: { rgb: '168, 85, 247', hex: '#a855f7' },   // Grape, Purple
    green: { rgb: '34, 197, 94', hex: '#22c55e' }      // Lime, Apple, Green
  };

  // ================================
  // State Management
  // ================================
  let activeProduct = '1';  // Currently active product (1 or 2)
  let activeVariantIndex = { '1': 0, '2': 0 };  // Active variant index per product

  // ================================
  // DOM Element References
  // ================================
  const productGlow = section.querySelector('[data-product-glow]');
  const backgroundGlow = section.querySelector('[data-background-glow]');
  const centerGlow = section.querySelector('[data-center-glow]');
  const shadowGlow = section.querySelector('[data-shadow-glow]');
  const imageGlow = section.querySelector('[data-image-glow]');
  const addToCartBtn = section.querySelector('[data-add-to-cart-btn]');
  const variantInput = section.querySelector('[data-variant-input]');
  const priceEl = section.querySelector('[data-product-price]');
  const productLabel = section.querySelector('[data-product-label]');
  const labelDot = section.querySelector('[data-label-dot]');

  // ================================
  // Update Glow Colors Function
  // ================================
  /**
   * Updates all glow effects and button colors based on selected flavor
   * @param {string} color - Color key from colorConfig (red, orange, blue, purple, green)
   */
  function updateGlowColor(color) {
    const { rgb, hex } = colorConfig[color] || colorConfig.red;
    
    // Get secondary color for gradient effects
    let secondaryRgb = '249, 115, 22'; // orange (default)
    if (color === 'orange') {
      secondaryRgb = '220, 38, 38'; // red
    } else if (color === 'blue') {
      secondaryRgb = '59, 130, 246'; // blue
    } else if (color === 'purple') {
      secondaryRgb = '168, 85, 247'; // purple
    } else if (color === 'green') {
      secondaryRgb = '34, 197, 94'; // green
    }
    
    // Update section background glow
    if (productGlow) {
      productGlow.style.background = `radial-gradient(ellipse at center, rgba(${rgb}, 0.15) 0%, transparent 70%)`;
    }
    
    // Update large background gradient behind image
    if (backgroundGlow) {
      backgroundGlow.style.background = `linear-gradient(to right, rgba(${rgb}, 0.3), rgba(${secondaryRgb}, 0.2))`;
    }
    
    // Update center glow effect
    if (centerGlow) {
      centerGlow.style.background = `radial-gradient(circle, rgba(${rgb}, 0.3) 0%, transparent 70%)`;
    }
    
    // Update shadow below product
    if (shadowGlow) {
      shadowGlow.style.background = `rgba(${rgb}, 0.3)`;
    }
    
    // Update product image glow (box-shadow)
    if (imageGlow) {
      imageGlow.style.boxShadow = `0 0 80px -10px rgba(${rgb}, 0.6)`;
    }
    
    // Update Add to Cart button background
    if (addToCartBtn) {
      addToCartBtn.style.backgroundColor = hex;
    }
    
    // Update product label text color
    if (productLabel) {
      // Remove all color classes
      ['red', 'orange', 'blue', 'purple', 'green'].forEach(c => {
        productLabel.classList.remove(`text-${c}-500`);
      });
      productLabel.classList.add(`text-${color}-500`);
    }
    
    // Update label dot color
    if (labelDot) {
      // Remove all color classes
      ['red', 'orange', 'blue', 'purple', 'green'].forEach(c => {
        labelDot.classList.remove(`bg-${c}-600`);
      });
      labelDot.classList.add(`bg-${color}-600`);
    }
  }

  // ================================
  // Switch Product Function
  // ================================
  /**
   * Switches between Product 1 and Product 2
   * @param {string} productNum - Product number ('1' or '2')
   */
  function switchProduct(productNum) {
    activeProduct = productNum;

    // Get the color from the product selector button, or fall back to first flavor button
    const productSelectBtn = section.querySelector(`[data-select-product="${productNum}"]`);
    const firstVariantBtn = section.querySelector(`[data-flavour-btn][data-product="${productNum}"]`);
    const productColor = (productSelectBtn && productSelectBtn.dataset.color) 
                        || (firstVariantBtn && firstVariantBtn.dataset.color) 
                        || 'red';
    const { rgb } = colorConfig[productColor] || colorConfig.red;

    // Update product selector buttons
    section.querySelectorAll('[data-select-product]').forEach(btn => {
      const isActive = btn.dataset.selectProduct === productNum;
      
      // Remove all color classes
      ['red', 'orange', 'blue', 'purple', 'green'].forEach(c => {
        btn.classList.remove(`border-${c}-500`, `bg-${c}-500/10`, `text-${c}-500`);
      });
      btn.style.boxShadow = '';
      
      if (isActive) {
        btn.classList.add(`border-${productColor}-500`, `bg-${productColor}-500/10`, `text-${productColor}-500`);
        btn.style.boxShadow = `0 0 15px rgba(${rgb}, 0.2)`;
        btn.classList.remove('border-white/10', 'text-gray-500');
      } else {
        btn.classList.add('border-white/10', 'text-gray-500');
      }
    });

    // Update product titles visibility
    section.querySelectorAll('[data-product-title]').forEach(title => {
      title.classList.toggle('hidden', title.dataset.productTitle !== productNum);
      title.classList.toggle('block', title.dataset.productTitle === productNum);
    });

    // Update product descriptions visibility
    section.querySelectorAll('[data-desc]').forEach(desc => {
      desc.classList.toggle('hidden', desc.dataset.desc !== productNum);
    });

    // Update flavour pickers visibility
    section.querySelectorAll('[data-flavour-picker]').forEach(picker => {
      picker.classList.toggle('hidden', picker.dataset.flavourPicker !== productNum);
    });

    // Update product images visibility
    section.querySelectorAll('[data-product]').forEach(img => {
      const isCurrentProduct = img.dataset.product === productNum;
      const isCurrentVariant = parseInt(img.dataset.variantIndex) === activeVariantIndex[productNum];
      img.classList.toggle('opacity-100', isCurrentProduct && isCurrentVariant);
      img.classList.toggle('opacity-0', !(isCurrentProduct && isCurrentVariant));
    });

    // Update variant input for cart form (if flavor buttons exist)
    const firstFlavourBtn = section.querySelector(`[data-flavour-btn][data-product="${productNum}"].border-red-500, [data-flavour-btn][data-product="${productNum}"].border-orange-500, [data-flavour-btn][data-product="${productNum}"].border-blue-500, [data-flavour-btn][data-product="${productNum}"].border-purple-500, [data-flavour-btn][data-product="${productNum}"].border-green-500`) ||
                            section.querySelector(`[data-flavour-btn][data-product="${productNum}"]`);
    if (firstFlavourBtn && variantInput) {
      variantInput.value = firstFlavourBtn.dataset.variantId;
      if (priceEl && firstFlavourBtn.dataset.variantPrice) {
        priceEl.textContent = firstFlavourBtn.dataset.variantPrice;
      }
    }

    // Update glow colors to match selected product
    // Use the product color we determined earlier (from product selector or flavor button)
    updateGlowColor(productColor);
  }

  // ================================
  // Switch Flavour/Variant Function
  // ================================
  /**
   * Switches between different flavors/variants of the active product
   * @param {HTMLElement} btn - The flavor button that was clicked
   */
  function switchFlavour(btn) {
    const productNum = btn.dataset.product;
    const variantIndex = parseInt(btn.dataset.variantIndex);
    const variantId = btn.dataset.variantId;
    const color = btn.dataset.color;

    activeVariantIndex[productNum] = variantIndex;

    // Update flavour buttons styling
    section.querySelectorAll(`[data-flavour-btn][data-product="${productNum}"]`).forEach(flavourBtn => {
      const btnColor = flavourBtn.dataset.color;
      const isActive = flavourBtn === btn;
      
      // Remove all color classes first
      ['red', 'orange', 'blue', 'purple', 'green'].forEach(c => {
        flavourBtn.classList.remove(`border-${c}-500`, `bg-${c}-500/10`, `text-${c}-500`);
      });
      
      // Remove old shadow styles
      flavourBtn.style.boxShadow = '';
      
      if (isActive) {
        flavourBtn.classList.add(`border-${btnColor}-500`, `bg-${btnColor}-500/10`, `text-${btnColor}-500`);
        // Apply dynamic shadow based on color
        const { rgb } = colorConfig[btnColor] || colorConfig.red;
        flavourBtn.style.boxShadow = `0 0 15px rgba(${rgb}, 0.2)`;
        flavourBtn.classList.remove('border-white/10', 'text-gray-500');
      } else {
        flavourBtn.classList.add('border-white/10', 'text-gray-500');
      }
    });

    // Update product images
    section.querySelectorAll(`[data-product="${productNum}"]`).forEach(img => {
      const isCurrentVariant = parseInt(img.dataset.variantIndex) === variantIndex;
      img.classList.toggle('opacity-100', isCurrentVariant);
      img.classList.toggle('opacity-0', !isCurrentVariant);
    });

    // Update variant input for cart
    if (variantInput) {
      variantInput.value = variantId;
    }

    // Update price display
    if (priceEl && btn.dataset.variantPrice) {
      priceEl.textContent = btn.dataset.variantPrice;
    }

    // Update all glow colors to match selected flavor
    updateGlowColor(color);
  }

  // ================================
  // Event Listeners
  // ================================
  
  // Product selector buttons (switch between Product 1 and Product 2)
  section.querySelectorAll('[data-select-product]').forEach(btn => {
    btn.addEventListener('click', () => {
      switchProduct(btn.dataset.selectProduct);
    });
  });

  // Flavour/variant buttons
  section.querySelectorAll('[data-flavour-btn]').forEach(btn => {
    btn.addEventListener('click', () => switchFlavour(btn));
  });

  // ================================
  // Initialization
  // ================================
  
  // Get initial color from product selector button (Product 1) or first flavor button
  const initialProductBtn = section.querySelector('[data-select-product="1"]');
  const initialFlavourBtn = section.querySelector('[data-flavour-btn][data-product="1"]');
  
  // Determine initial color: prefer flavor button, fallback to product selector, then default red
  const initialColor = (initialFlavourBtn && initialFlavourBtn.dataset.color)
                      || (initialProductBtn && initialProductBtn.dataset.color)
                      || 'red';
  
  // Apply initial glow color
  updateGlowColor(initialColor);
  
  // Set initial shadow for first flavor button (if exists)
  if (initialFlavourBtn) {
    const { rgb } = colorConfig[initialColor] || colorConfig.red;
    initialFlavourBtn.style.boxShadow = `0 0 15px rgba(${rgb}, 0.2)`;
  }
  
  // Set initial shadow for product selector button (Product 1)
  if (initialProductBtn) {
    const { rgb } = colorConfig[initialColor] || colorConfig.red;
    initialProductBtn.style.boxShadow = `0 0 15px rgba(${rgb}, 0.2)`;
  }
  
}

// Initialize VP Product Section when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVPProductSection);
} else {
  // DOM already loaded, run immediately
  initVPProductSection();
}

// Also initialize on Shopify section load/reload (for theme editor)
document.addEventListener('shopify:section:load', initVPProductSection);

// ================================
// VP SINGLE PRODUCT - FULL PRODUCT PAGE
// ================================
/**
 * VP Single Product Section
 * Full product page with:
 * - Variant/flavor selection with dynamic color changes
 * - Image gallery with thumbnails
 * - Quantity controls
 * - Accordion sections
 * - Trust badges with accent colors
 * - Customizable colors via CSS variables
 */
function initVPProductPage() {
  const sections = document.querySelectorAll('[data-product-page]');
  
  sections.forEach(section => {
    // Skip if already initialized
    if (section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';
    
    // Get variant data from global Shopify object or section-specific data
    const sectionId = section.id;
    const variantDataEl = section.querySelector('[data-variant-json]');
    let productVariants = [];
    let productOptions = [];
    
    if (variantDataEl) {
      try {
        const data = JSON.parse(variantDataEl.textContent);
        productVariants = data.variants || [];
        productOptions = data.options || [];
      } catch (e) {
        console.error('Failed to parse variant data:', e);
      }
    }
    
    // DOM Elements
    const variantInput = section.querySelector('[data-variant-input]');
    const priceElement = section.querySelector('[data-product-price]');
    const addToCartBtn = section.querySelector('[data-add-to-cart]');
    const addToCartText = section.querySelector('[data-add-to-cart-text]');
    const mainImage = section.querySelector('[data-main-image]');
    const bgGlow = section.querySelector('[data-bg-glow]');
    const quantityInput = section.querySelector('[data-quantity-input]');
    const quantityMinus = section.querySelector('[data-quantity-minus]');
    const quantityPlus = section.querySelector('[data-quantity-plus]');
    const thumbnailBtns = section.querySelectorAll('[data-thumbnail-btn]');
    const optionBtns = section.querySelectorAll('[data-option-btn]');
    const accordionTriggers = section.querySelectorAll('[data-accordion-trigger]');
    const accentElements = section.querySelectorAll('[data-accent-text]');
    const accentIcons = section.querySelectorAll('[data-accent-icon]');
    const accentBg = section.querySelector('[data-accent-bg]');

    // Current selections
    let selectedOptions = {};
    productOptions.forEach(opt => {
      selectedOptions[opt.name] = opt.selected_value;
    });

    // Color mapping for Tailwind classes (extended for variant buttons)
    const colorMap = {
      red: { text: 'text-red-400', border: 'border-red-500', bg: 'bg-red-500', glow: 'from-red-900/20', ring: 'ring-red-500/50', shadow: 'shadow-red-500/30' },
      orange: { text: 'text-orange-400', border: 'border-orange-500', bg: 'bg-orange-500', glow: 'from-orange-900/20', ring: 'ring-orange-500/50', shadow: 'shadow-orange-500/30' },
      blue: { text: 'text-blue-400', border: 'border-blue-500', bg: 'bg-blue-500', glow: 'from-blue-900/20', ring: 'ring-blue-500/50', shadow: 'shadow-blue-500/30' },
      purple: { text: 'text-purple-400', border: 'border-purple-500', bg: 'bg-purple-500', glow: 'from-purple-900/20', ring: 'ring-purple-500/50', shadow: 'shadow-purple-500/30' },
      green: { text: 'text-emerald-400', border: 'border-emerald-500', bg: 'bg-emerald-500', glow: 'from-emerald-900/20', ring: 'ring-emerald-500/50', shadow: 'shadow-emerald-500/30' },
      emerald: { text: 'text-emerald-400', border: 'border-emerald-500', bg: 'bg-emerald-500', glow: 'from-emerald-900/20', ring: 'ring-emerald-500/50', shadow: 'shadow-emerald-500/30' },
      yellow: { text: 'text-yellow-400', border: 'border-yellow-500', bg: 'bg-yellow-500', glow: 'from-yellow-900/20', ring: 'ring-yellow-500/50', shadow: 'shadow-yellow-500/30' },
      pink: { text: 'text-pink-400', border: 'border-pink-500', bg: 'bg-pink-500', glow: 'from-pink-900/20', ring: 'ring-pink-500/50', shadow: 'shadow-pink-500/30' },
      cyan: { text: 'text-cyan-400', border: 'border-cyan-500', bg: 'bg-cyan-500', glow: 'from-cyan-900/20', ring: 'ring-cyan-500/50', shadow: 'shadow-cyan-500/30' },
      gray: { text: 'text-gray-400', border: 'border-gray-600', bg: 'bg-gray-800', glow: 'from-gray-900/20', ring: 'ring-gray-500/50', shadow: 'shadow-gray-500/30' },
      white: { text: 'text-white', border: 'border-white', bg: 'bg-white', glow: 'from-white/20', ring: 'ring-white/50', shadow: 'shadow-white/30' }
    };
    
    // All possible color classes to remove when switching
    const allColorClasses = {
      text: ['text-red-400', 'text-orange-400', 'text-blue-400', 'text-purple-400', 'text-emerald-400', 'text-yellow-400', 'text-pink-400', 'text-cyan-400', 'text-gray-400', 'text-white', 'text-red-500', 'text-green-400'],
      border: ['border-red-500', 'border-orange-500', 'border-blue-500', 'border-purple-500', 'border-emerald-500', 'border-yellow-500', 'border-pink-500', 'border-cyan-500', 'border-gray-600', 'border-white', 'border-green-500'],
      bg: ['bg-red-500', 'bg-orange-500', 'bg-blue-500', 'bg-purple-500', 'bg-emerald-500', 'bg-yellow-500', 'bg-pink-500', 'bg-cyan-500', 'bg-gray-800', 'bg-white', 'bg-green-500'],
      ring: ['ring-red-500/50', 'ring-orange-500/50', 'ring-blue-500/50', 'ring-purple-500/50', 'ring-emerald-500/50', 'ring-yellow-500/50', 'ring-pink-500/50', 'ring-cyan-500/50', 'ring-gray-500/50', 'ring-white/50', 'ring-green-500/50'],
      shadow: ['shadow-red-500/30', 'shadow-orange-500/30', 'shadow-blue-500/30', 'shadow-purple-500/30', 'shadow-emerald-500/30', 'shadow-yellow-500/30', 'shadow-pink-500/30', 'shadow-cyan-500/30', 'shadow-gray-500/30', 'shadow-white/30', 'shadow-green-500/30'],
      glow: ['from-red-900/20', 'from-orange-900/20', 'from-blue-900/20', 'from-purple-900/20', 'from-emerald-900/20', 'from-yellow-900/20', 'from-pink-900/20', 'from-cyan-900/20', 'from-gray-900/20', 'from-white/20', 'from-green-900/20']
    };

    let currentColor = 'red';

    // Find matching variant
    function findVariant() {
      return productVariants.find(variant => {
        return variant.options.every((opt, index) => {
          const optionName = productOptions[index]?.name;
          return selectedOptions[optionName] === opt;
        });
      });
    }

    // Update colors throughout the page
    function updateColors(color) {
      if (!colorMap[color]) color = 'red';
      const colors = colorMap[color];
      
      // Update accent text elements - remove all possible colors first
      accentElements.forEach(el => {
        allColorClasses.text.forEach(cls => el.classList.remove(cls));
        el.classList.add(colors.text);
      });
      
      // Update accent icons
      accentIcons.forEach(el => {
        allColorClasses.text.forEach(cls => el.classList.remove(cls));
        el.classList.add(colors.text);
      });
      
      // Update add to cart button
      if (accentBg) {
        allColorClasses.bg.forEach(cls => accentBg.classList.remove(cls));
        accentBg.classList.add(colors.bg);
      }
      
      // Update background glow
      if (bgGlow) {
        allColorClasses.glow.forEach(cls => bgGlow.classList.remove(cls));
        bgGlow.classList.add(colors.glow, 'to-transparent');
      }
      
      currentColor = color;
    }

    // Update variant
    function updateVariant(variant) {
      if (!variant) return;
      
      // Update hidden input
      if (variantInput) {
        variantInput.value = variant.id;
      }
      
      // Update price
      if (priceElement) {
        const formattedPrice = (typeof Shopify !== 'undefined' && Shopify.formatMoney) ? 
          Shopify.formatMoney(variant.price) : 
          '$' + (variant.price / 100).toFixed(2);
        priceElement.textContent = formattedPrice;
      }
      
      // Update availability
      if (addToCartBtn && addToCartText) {
        if (variant.available) {
          addToCartBtn.disabled = false;
          addToCartText.textContent = 'Add To Cart';
        } else {
          addToCartBtn.disabled = true;
          addToCartText.textContent = 'Sold Out';
        }
      }
      
      // Update main image if variant has image
      if (mainImage && variant.featured_image) {
        mainImage.classList.remove('loaded');
        mainImage.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_800x.$1');
        mainImage.onload = () => mainImage.classList.add('loaded');
      }
      
      // Update URL
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    // Option button click handler
    optionBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const optionName = this.dataset.optionName;
        const optionValue = this.dataset.optionValue;
        // Get variant color from data attributes
        const variantColor = this.dataset.variantColor || this.dataset.flavorColor || 'red';
        const variantBorder = this.dataset.variantBorder;
        const variantText = this.dataset.variantText;
        const variantGlow = this.dataset.variantGlow;
        const variantRing = this.dataset.variantRing;
        const variantBg = this.dataset.variantBg;
        
        // Update selection
        selectedOptions[optionName] = optionValue;
        
        // Update selected option display with matching color
        const selectedDisplay = section.querySelector(`[data-selected-option="${optionName}"]`);
        if (selectedDisplay) {
          selectedDisplay.textContent = optionValue;
          // Remove all color classes and add the new one
          allColorClasses.text.forEach(cls => selectedDisplay.classList.remove(cls));
          if (variantText) {
            selectedDisplay.classList.add(variantText);
          } else if (colorMap[variantColor]) {
            selectedDisplay.classList.add(colorMap[variantColor].text);
          }
        }
        
        // Update button states
        const siblingBtns = section.querySelectorAll(`[data-option-name="${optionName}"]`);
        siblingBtns.forEach(sib => {
          // Remove all active styling classes
          allColorClasses.border.forEach(cls => sib.classList.remove(cls));
          allColorClasses.text.forEach(cls => sib.classList.remove(cls));
          allColorClasses.ring.forEach(cls => sib.classList.remove(cls));
          allColorClasses.shadow.forEach(cls => sib.classList.remove(cls));
          sib.classList.remove('bg-black/60', 'shadow-lg', 'ring-2');
          
          // Reset to inactive state
          sib.classList.add('border-white/10', 'bg-white/5', 'text-gray-500');
          
          // Update color dot indicator
          const colorDot = sib.querySelector('.rounded-full');
          if (colorDot) {
            colorDot.classList.remove('opacity-100', 'scale-100');
            colorDot.classList.add('opacity-40', 'scale-75');
          }
          
          // Update bottom accent bar
          const accentBar = sib.querySelectorAll('span')[2]; // Third span is the accent bar
          if (accentBar && accentBar.classList.contains('absolute') && accentBar.classList.contains('bottom-0')) {
            accentBar.classList.remove('opacity-100');
            accentBar.classList.add('opacity-0');
          }
        });
        
        // Add active classes to clicked button
        this.classList.remove('border-white/10', 'bg-white/5', 'text-gray-500');
        
        // Apply color-specific active styling
        if (variantBorder) this.classList.add(variantBorder);
        if (variantText) this.classList.add(variantText);
        if (variantGlow) this.classList.add(variantGlow);
        if (variantRing) this.classList.add(variantRing, 'ring-2');
        this.classList.add('bg-black/60', 'shadow-lg');
        
        // Show color dot indicator
        const colorDot = this.querySelector('.rounded-full');
        if (colorDot) {
          colorDot.classList.remove('opacity-40', 'scale-75');
          colorDot.classList.add('opacity-100', 'scale-100');
        }
        
        // Show bottom accent bar
        const accentBar = this.querySelectorAll('span')[2];
        if (accentBar && accentBar.classList.contains('absolute') && accentBar.classList.contains('bottom-0')) {
          accentBar.classList.remove('opacity-0');
          accentBar.classList.add('opacity-100');
        }
        
        // Update colors throughout page
        updateColors(variantColor);
        
        // Find and update variant
        const variant = findVariant();
        if (variant) {
          updateVariant(variant);
        }
      });
    });

    // Thumbnail click handler
    thumbnailBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const imageUrl = this.dataset.imageUrl;
        
        // Update main image
        if (mainImage && imageUrl) {
          mainImage.classList.remove('loaded');
          mainImage.src = imageUrl;
          mainImage.onload = () => mainImage.classList.add('loaded');
        }
        
        // Update active thumbnail
        thumbnailBtns.forEach(tb => {
          allColorClasses.border.forEach(cls => tb.classList.remove(cls));
          tb.classList.add('border-white/10');
        });
        this.classList.remove('border-white/10');
        if (colorMap[currentColor]) {
          this.classList.add(colorMap[currentColor].border);
        }
      });
    });

    // Quantity controls
    if (quantityMinus && quantityPlus && quantityInput) {
      quantityMinus.addEventListener('click', () => {
        const current = parseInt(quantityInput.value) || 1;
        if (current > 1) {
          quantityInput.value = current - 1;
        }
      });
      
      quantityPlus.addEventListener('click', () => {
        const current = parseInt(quantityInput.value) || 1;
        quantityInput.value = current + 1;
      });
      
      quantityInput.addEventListener('change', () => {
        const value = parseInt(quantityInput.value);
        if (isNaN(value) || value < 1) {
          quantityInput.value = 1;
        }
      });
    }

    // Accordion functionality
    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', function() {
        const accordionId = this.dataset.accordionTrigger;
        const content = section.querySelector(`[data-accordion-content="${accordionId}"]`);
        const icon = section.querySelector(`[data-accordion-icon="${accordionId}"]`);
        
        if (content && icon) {
          const isOpen = content.classList.contains('active');
          
          // Toggle current
          if (isOpen) {
            content.classList.remove('active');
            icon.classList.add('rotate-180');
          } else {
            content.classList.add('active');
            icon.classList.remove('rotate-180');
          }
        }
      });
    });

    // Initialize first active thumbnail border color
    const firstThumb = section.querySelector('[data-thumbnail-btn][data-index="0"]');
    if (firstThumb && colorMap[currentColor]) {
      allColorClasses.border.forEach(cls => firstThumb.classList.remove(cls));
      firstThumb.classList.add(colorMap[currentColor].border);
    }
    
    // Update accent button background with CSS variable fallback
    if (accentBg) {
      const computedStyle = getComputedStyle(section);
      const btnBgColor = computedStyle.getPropertyValue('--vp-btn-bg').trim();
      if (btnBgColor) {
        accentBg.style.backgroundColor = btnBgColor;
      }
    }
  });
}

// Initialize VP Product Page when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVPProductPage);
} else {
  initVPProductPage();
}

// Also initialize on Shopify section load/reload (for theme editor)
document.addEventListener('shopify:section:load', initVPProductPage);
